import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import {
  PageHeader,
  Spin,
  Upload,
  Row,
  Col,
  message,
  Alert,
  Switch,
  Collapse,
  Progress,
} from "antd";
import { UploadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
// Sólo para ESCRIBIR la plantilla: el writer de `xlsx` ignora la `s` de cada celda,
// así que una plantilla con estilos se descargaba en blanco y negro. La lectura del
// archivo que sube el usuario se queda con `xlsx`, que es lo que ya se usaba.
import * as XLSXStyle from "xlsx-js-style";
import { Form, FormItem, TableItem, ButtonItem } from "./Items";

/**
 * Importador genérico desde Excel.
 *
 * Props:
 *  - titulo                 (string)   título del PageHeader
 *  - nombreEntidadPlural    (string)   p.ej. "niveles educativos"
 *  - endpointDatosImportar  (string)   ruta para precargar catálogos (puede ser null si no hay lookups)
 *  - endpointImportar       (string)   ruta destino del POST final
 *  - construirPlantilla     (fn(catalogos) => XLSX workbook) construye el workbook a descargar
 *  - validarFila            (fn(fila, catalogos, indice, contexto) => resultado)
 *      Resultado básico:
 *        { ok: true, datos } | { ok: false, errores: [{campo, motivo}] }
 *      Resultado extendido (cuando permitirEdicion=true):
 *        { ok: true, accion: "crear",       datos }
 *        { ok: true, accion: "editar",      datos, idExistente, cambios: [colKey,...], valoresAnteriores: {colKey: prev,...} }
 *        { ok: true, accion: "sinCambios",  datos, idExistente, valoresAnteriores }
 *        { ok: true, accion: "error",       datos }
 *            La fila no se manda al servidor, pero SÍ se muestra en la tabla con sus datos
 *            resueltos. Se usa cuando el problema es de configuración (no del archivo) y el
 *            usuario necesita ver de quién es y por qué, no sólo un conteo de errores.
 *            Con `ok: false` la fila sólo existiría en errores.xlsx.
 *  - columnasPreview        (array)    columnas de Ant Design Table para el preview
 *  - nombrePlantilla        (string)   p.ej. "plantillaNivelesEducativos.xlsx"
 *  - prepararCatalogos      (fn(resData) => catalogos)  opcional
 *  - prepararPayloadExtra   (fn() => objeto)            opcional
 *  - permitirEdicion        (bool)     opt-in: activa modo add+update
 *  - mostrarSwitchSoloCambios (bool)   muestra switch "Mostrar solo con cambios"
 *  - controlesExtra         (node)     contenido extra renderizado dentro del
 *      cuerpo (debajo de las instrucciones), no encima del PageHeader
 *  - onImportSuccess        (fn(resData) => bool)  opcional: se llama tras una
 *      importación exitosa. Si devuelve true, el importador NO navega hacia
 *      atrás automáticamente (el consumidor toma el control, p.ej. para
 *      mostrar un modal de resumen)
 *  - antesDeImportar        (fn() => bool)  opcional: se llama al presionar
 *      "Importar"; si devuelve false se cancela el envío (para validaciones
 *      propias de la pantalla, p.ej. selects obligatorios)
 *  - tamanoLote             (number)  opcional: parte el envío en bloques de ese
 *      tamaño y los manda EN SERIE, con barra de progreso. Se usa cuando cada fila
 *      es un proceso pesado del lado del servidor (p.ej. crear una venta con póliza,
 *      inventario y correo) y una sola petición se caería por timeout. La respuesta
 *      que recibe onImportSuccess es la agregación de todos los bloques
 *      (`numero`/`numeroCreados`/`numeroEditados`/`repetidos` sumados y `resultados`
 *      concatenados). Si un bloque falla, se detiene ahí y el agregado lleva
 *      `incompleto: true`. Sin esta prop el comportamiento es el de siempre:
 *      una sola petición con todo.
 *  - etiquetasAccion        ({crear, editar, sinCambios})  opcional: reemplaza los
 *      textos de la columna "Acción" y del Alert de resumen. Una etiqueta en null
 *      se omite del Alert (útil cuando esa acción no aplica en esa pantalla).
 *  - enviarImportacion      (fn(payload))  opcional: la pantalla se encarga del envío.
 *      Al presionar "Importar" se arma el mismo payload de siempre y se le entrega,
 *      en vez de mandarlo aquí. Sirve cuando la importación corre en segundo plano
 *      en el servidor (lanzarProcesoMasivo) y la pantalla sigue su avance y muestra
 *      sus resultados. Tiene prioridad sobre `tamanoLote`.
 *
 */
class ImportadorExcelGenerico extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    archivosExcel: [],
    data: [],
    archivoError: null,
    catalogos: {},
    erroresResumen: null,
    soloConCambios: true,
    resumenAcciones: null,
    progreso: null,
  };

  componentDidMount() {
    if (!this.props.endpointDatosImportar) {
      return;
    }
    this.setState({ loading: true });
    axios
      .post(
        this.props.endpointDatosImportar,
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken },
      )
      .then((res) => {
        if (res.data.success === 2) {
          const catalogos = this.props.prepararCatalogos
            ? this.props.prepararCatalogos(res.data)
            : res.data;
          this.setState({ catalogos, loading: false });
        } else {
          this.setState({ loading: false });
          message.error(
            res.data.message ||
              "No se pudieron cargar los catálogos para validar el Excel",
          );
        }
      })
      .catch((err) => {
        // El detalle del fallo casi nunca está en err.message: viene en la respuesta.
        console.log(err.response?.data || err);
        this.setState({ loading: false });
        message.error(
          err.response?.data?.message ||
            (err.response?.status
              ? `El servidor respondió ${err.response.status} al cargar catálogos`
              : `Error cargando catálogos: ${err.message}`),
        );
      });
  }

  atras = () => {
    if (this.props.location?.state?.volverA) {
      this.props.history.replace(this.props.location.state.volverA);
    } else {
      this.props.history.goBack();
    }
  };

  onFinishFailed = () => {
    message.error("Por favor revisa los datos antes de importar");
  };

  stripPrivado = (row) => {
    const obj = {};
    for (const key in row) {
      if (key.startsWith("_")) continue;
      obj[key] = row[key];
    }
    return obj;
  };

  // Separa las filas válidas en las dos listas que espera el backend.
  // Devuelve null cuando no hay nada que mandar (ya avisó con un message).
  construirListasEnvio = () => {
    const { permitirEdicion } = this.props;
    const data = this.state.data || [];

    if (data.length === 0) {
      message.warning("No hay filas válidas para importar");
      return null;
    }

    if (!permitirEdicion) {
      return { crear: data.map((d) => this.stripPrivado(d)), editar: [] };
    }

    const crear = data
      .filter((d) => d._accion === "crear")
      .map((d) => this.stripPrivado(d));
    const editar = data
      .filter((d) => d._accion === "editar")
      .map((d) => ({
        idExistente: d._idExistente,
        ...this.stripPrivado(d),
      }));

    if (crear.length === 0 && editar.length === 0) {
      message.warning("No hay filas a crear ni a editar");
      return null;
    }

    return { crear, editar };
  };

  // El backend en modo NO edición recibe las filas en `data`, no en `crear`.
  armarPayload = (crear, editar, extra) =>
    this.props.permitirEdicion
      ? { empresaId: this.props.empresaId, crear, editar, ...extra }
      : { empresaId: this.props.empresaId, data: crear, ...extra };

  mensajeExito = (resData, totalFilas) => {
    const { permitirEdicion, nombreEntidadPlural } = this.props;
    const ent = nombreEntidadPlural || "registros";
    if (permitirEdicion) {
      const c = resData.numeroCreados ?? 0;
      const e = resData.numeroEditados ?? 0;
      if (c > 0 && e > 0) {
        message.success(`Se crearon ${c} y se editaron ${e} ${ent}`);
      } else if (c > 0) {
        message.success(`Se crearon ${c} ${ent}`);
      } else if (e > 0) {
        message.success(`Se editaron ${e} ${ent}`);
      } else if (resData.numero) {
        message.success(`Se importaron ${resData.numero} ${ent}`);
      } else {
        message.success("No hubo cambios para aplicar");
      }
    } else {
      const total = resData.numero ?? totalFilas;
      message.success(`Se importaron ${total} ${ent}`);
    }
    if (resData.repetidos) {
      message.warning(`Se omitieron ${resData.repetidos} duplicados`);
    }
  };

  // Permite que el consumidor intercepte el resultado (p.ej. mostrar
  // un modal de resumen). Si maneja el resultado devolviendo true,
  // no navegamos hacia atrás automáticamente.
  cerrarImportacion = (resData) => {
    const manejado = this.props.onImportSuccess
      ? this.props.onImportSuccess(resData)
      : false;
    if (!manejado) {
      this.atras();
    }
  };

  // Envío por bloques: cada bloque es una petición independiente y se mandan en
  // serie. Se usa cuando el backend hace trabajo pesado por fila y no aguanta el
  // archivo completo en una sola petición.
  enviarEnLotes = async (crear, editar, extra) => {
    const { tamanoLote, nombreEntidadPlural } = this.props;

    // Se mezclan las dos listas en un solo arreglo para poder cortarlo en bloques
    // sin perder de qué lista viene cada fila.
    const items = [
      ...crear.map((fila) => ({ tipo: "crear", fila })),
      ...editar.map((fila) => ({ tipo: "editar", fila })),
    ];

    const bloques = [];
    for (let i = 0; i < items.length; i += tamanoLote) {
      bloques.push(items.slice(i, i + tamanoLote));
    }

    const agregado = {
      success: 2,
      numero: 0,
      numeroCreados: 0,
      numeroEditados: 0,
      repetidos: 0,
      resultados: [],
    };

    this.setState({ loading: true, progreso: { hechas: 0, total: items.length } });

    let procesadas = 0;

    for (const bloque of bloques) {
      const payload = this.armarPayload(
        bloque.filter((i) => i.tipo === "crear").map((i) => i.fila),
        bloque.filter((i) => i.tipo === "editar").map((i) => i.fila),
        extra,
      );

      let res;
      try {
        res = await axios.post(this.props.endpointImportar, payload, {
          headers: this.props.headersToken,
        });
      } catch (err) {
        // El detalle del fallo casi nunca está en err.message: viene en la respuesta del
        // servidor (o no hay respuesta y entonces sí fue la red).
        console.log(err.response?.data || err);
        agregado.incompleto = true;
        agregado.mensajeError = err.response?.data?.message
          ? err.response.data.message
          : err.response?.status
            ? `El servidor respondió ${err.response.status} (${err.response.statusText || "sin detalle"})`
            : `Error de red al importar: ${err.message}`;
        break;
      }

      if (res.data.success !== 2) {
        agregado.incompleto = true;
        agregado.mensajeError =
          [res.data.message, res.data.description].filter(Boolean).join(" — ") ||
          `No se pudieron importar los ${nombreEntidadPlural || "registros"} (el servidor respondió success ${res.data.success} sin mensaje; revisa el log del servidor)`;
        break;
      }

      agregado.numero += res.data.numero ?? 0;
      agregado.numeroCreados += res.data.numeroCreados ?? 0;
      agregado.numeroEditados += res.data.numeroEditados ?? 0;
      agregado.repetidos += res.data.repetidos ?? 0;
      if (Array.isArray(res.data.resultados)) {
        agregado.resultados.push(...res.data.resultados);
      }

      procesadas += bloque.length;
      this.setState({
        progreso: { hechas: procesadas, total: items.length },
      });
    }

    agregado.procesadas = procesadas;
    agregado.totalFilas = items.length;

    this.setState({ loading: false, progreso: null });

    if (agregado.incompleto) {
      message.error(
        `${agregado.mensajeError}. Se procesaron ${procesadas} de ${items.length} filas.`,
      );
      // No se navega hacia atrás en un envío que quedó a medias: la pantalla que
      // sepa mostrar el detalle lo hace, y si no, el usuario se queda donde está.
      if (this.props.onImportSuccess) this.props.onImportSuccess(agregado);
      return;
    }

    this.mensajeExito(agregado, items.length);
    this.cerrarImportacion(agregado);
  };

  onFinish = () => {
    const { nombreEntidadPlural, tamanoLote } = this.props;

    if (this.props.antesDeImportar && this.props.antesDeImportar() === false) {
      return;
    }

    const listas = this.construirListasEnvio();
    if (!listas) return;

    const extra = this.props.prepararPayloadExtra
      ? this.props.prepararPayloadExtra()
      : {};

    if (this.props.enviarImportacion) {
      this.props.enviarImportacion(this.armarPayload(listas.crear, listas.editar, extra));
      return;
    }

    if (tamanoLote > 0) {
      this.enviarEnLotes(listas.crear, listas.editar, extra);
      return;
    }

    const totalFilas = listas.crear.length + listas.editar.length;
    const payload = this.armarPayload(listas.crear, listas.editar, extra);

    this.setState({ loading: true });
    axios
      .post(this.props.endpointImportar, payload, {
        headers: this.props.headersToken,
      })
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success === 2) {
          this.mensajeExito(res.data, totalFilas);
          this.cerrarImportacion(res.data);
        } else if (res.data.success === 1) {
          message.error(
            [res.data.message, res.data.description].filter(Boolean).join(" — ") ||
              `No se pudieron importar los ${nombreEntidadPlural || "registros"}`,
          );
        } else {
          message.error(
            `Hubo un error y no se importaron los ${nombreEntidadPlural || "registros"}. Revisa el log del servidor.`,
          );
        }
      })
      .catch((err) => {
        console.log(err.response?.data || err);
        this.setState({ loading: false });
        message.error(
          err.response?.data?.message ||
            (err.response?.status
              ? `El servidor respondió ${err.response.status} al importar`
              : `Error de red al importar: ${err.message}`),
        );
      });
  };

  onChangeArchivo = (e) => {
    if (
      e.file.type !==
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      message.error("El archivo debe ser un .xlsx");
      return;
    }

    this.setState({
      loading: true,
      archivosExcel: [
        {
          ...e.file,
          name: e.file.name,
          status: "uploading",
          response: "Cargando",
        },
      ],
      archivoError: null,
      erroresResumen: null,
      resumenAcciones: null,
    });
    this.cargarArchivoExcel(e.file);
  };

  cargarArchivoExcel = (file) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);
    reader.onloadend = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const hojas = workbook.SheetNames.map((sheetName) => ({
          sheetName,
          data: XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName]),
        }));
        this.archivoVerificar(hojas, file);
      } catch (err) {
        console.log(err);
        message.error("No se pudo leer el archivo Excel");
        this.setState({ loading: false });
      }
    };
  };

  archivoVerificar = async (hojas, file) => {
    const { permitirEdicion } = this.props;
    const filas = hojas[0]?.data || [];
    if (filas.length === 0) {
      message.warning("La primera hoja del Excel está vacía");
      this.setState({
        loading: false,
        archivosExcel: [{ ...file, name: file.name, status: "success" }],
        data: [],
      });
      return;
    }

    const validas = [];
    const filasConError = [];
    let totalErrores = 0;
    const contexto = {
      clavesYaUsadas: new Map(),
      hojas, // [{ sheetName, data }]
    };

    if (typeof this.props.prepararContextoArchivo === "function") {
      try {
        const extra = await this.props.prepararContextoArchivo(
          hojas,
          this.state.catalogos,
        );
        if (extra && typeof extra === "object") {
          Object.assign(contexto, extra);
        }
      } catch (err) {
        console.log(err);
        message.error("No se pudieron procesar las hojas del Excel");
        this.setState({ loading: false });
        return;
      }
    }

    for (let i = 0; i < filas.length; i++) {
      const fila = filas[i];
      const numeroFila = (fila["__rowNum__"] ?? i) + 1;
      let resultado;
      try {
        resultado = await this.props.validarFila(
          fila,
          this.state.catalogos,
          i,
          contexto,
        );
      } catch (err) {
        console.log(err);
        resultado = {
          ok: false,
          errores: [{ campo: "—", motivo: "Error inesperado al validar la fila" }],
        };
      }

      if (resultado.ok) {
        if (permitirEdicion) {
          const accion = resultado.accion || "crear";
          const cambios = resultado.cambios || [];
          const valoresAnteriores = resultado.valoresAnteriores || {};
          const filaAnotada = {
            ...resultado.datos,
            _accion: accion,
            _cambios: cambios,
            _idExistente: resultado.idExistente || null,
          };
          for (const col of Object.keys(valoresAnteriores)) {
            filaAnotada[`_anterior_${col}`] = valoresAnteriores[col];
          }
          validas.push(filaAnotada);
        } else {
          validas.push(resultado.datos);
        }
      } else {
        totalErrores += resultado.errores.length;
        const filaError = { ...fila };
        filaError["__Errores__"] = resultado.errores
          .map((er) => `${er.campo}: ${er.motivo}`)
          .join(" | ");
        filaError["__Fila__"] = numeroFila;
        filasConError.push(filaError);
      }
    }

    let archivoError = null;
    if (filasConError.length > 0) {
      archivoError = XLSX.utils.book_new();
      const hoja = XLSX.utils.json_to_sheet(filasConError);
      XLSX.utils.book_append_sheet(archivoError, hoja, "Errores");
    }

    let resumenAcciones = null;
    if (permitirEdicion) {
      const numCrear = validas.filter((d) => d._accion === "crear").length;
      const numEditar = validas.filter((d) => d._accion === "editar").length;
      const numSinCambios = validas.filter((d) => d._accion === "sinCambios").length;
      // Las filas con accion "error" sí se muestran en la tabla; las de `ok: false` sólo
      // existen en errores.xlsx. Las dos cuentan como errores en el resumen.
      const numError = validas.filter((d) => d._accion === "error").length;
      resumenAcciones = {
        crear: numCrear,
        editar: numEditar,
        sinCambios: numSinCambios,
        errores: filasConError.length + numError,
      };
    }

    this.setState({
      loading: false,
      archivosExcel: [
        {
          ...file,
          name: file.name,
          status: "success",
          response: filasConError.length > 0 ? "Con errores" : "Listo",
        },
      ],
      data: validas,
      archivoError,
      erroresResumen:
        filasConError.length > 0
          ? {
              filasConError: filasConError.length,
              totalErrores,
              filasValidas: validas.length,
              filasOriginales: filas.length,
            }
          : null,
      resumenAcciones,
    });

    if (filasConError.length > 0) {
      message.error(
        `Se detectaron ${totalErrores} errores en ${filasConError.length} filas`,
      );
    }
  };

  descargarArchivoErrores = () => {
    if (!this.state.archivoError) return;
    const wbout = XLSX.write(this.state.archivoError, {
      bookType: "xlsx",
      type: "binary",
    });
    const s2ab = (s) => {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    };
    const blob = new Blob([s2ab(wbout)], { type: "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style = "display: none";
    a.href = url;
    a.download = "errores.xlsx";
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  descargarPlantilla = () => {
    try {
      const workbook = this.props.construirPlantilla(this.state.catalogos);
      XLSXStyle.writeFile(
        workbook,
        this.props.nombrePlantilla || "plantilla.xlsx",
      );
    } catch (err) {
      console.log(err);
      message.error("No se pudo generar la plantilla");
    }
  };

  envolverColumnasConHighlight = (columnas) => {
    return columnas.map((col) => {
      const colKey = col.key || col.dataIndex;
      const renderOriginal = col.render;
      return {
        ...col,
        render: (value, record, index) => {
          const cambios = record._cambios || [];
          const changed = cambios.includes(colKey);
          const contenido = renderOriginal
            ? renderOriginal(value, record, index)
            : record[col.dataIndex] !== undefined
              ? record[col.dataIndex]
              : value;
          if (!changed) {
            return contenido;
          }
          const previo = record[`_anterior_${colKey}`];
          const tieneAnterior =
            previo !== undefined && previo !== null && previo !== "";
          return (
            <div
              style={{
                borderLeft: "3px solid #faad14",
                paddingLeft: 8,
                lineHeight: 1.25,
              }}
            >
              <div style={{ color: "#262626", fontWeight: 500 }}>
                {contenido}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#bfbfbf",
                  textDecoration: "line-through",
                  marginTop: 2,
                }}
              >
                {tieneAnterior ? previo : "(vacío)"}
              </div>
            </div>
          );
        },
      };
    });
  };

  renderInstrucciones() {
    const { instrucciones } = this.props;
    if (!instrucciones) return null;

    let contenido;
    if (Array.isArray(instrucciones)) {
      contenido = (
        <ol style={{ paddingLeft: 20, margin: 0 }}>
          {instrucciones.map((paso, i) => (
            <li key={i} style={{ marginBottom: 6 }}>
              {paso}
            </li>
          ))}
        </ol>
      );
    } else {
      contenido = instrucciones;
    }

    return (
      <Collapse
        defaultActiveKey={["instrucciones"]}
        bordered={false}
        style={{
          marginBottom: 12,
          background: "#fafafa",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <Collapse.Panel
          key="instrucciones"
          style={{ border: "none", background: "transparent" }}
          header={
            <span style={{ fontWeight: 500 }}>
              <InfoCircleOutlined
                style={{ color: "#1890ff", marginRight: 8 }}
              />
              Instrucciones
            </span>
          }
        >
          {contenido}
        </Collapse.Panel>
      </Collapse>
    );
  }

  render() {
    const { titulo, columnasPreview = [], permitirEdicion, mostrarSwitchSoloCambios } = this.props;
    const { erroresResumen, resumenAcciones, soloConCambios, progreso } = this.state;

    const etiquetas = {
      crear: "Crear",
      editar: "Editar",
      sinCambios: "Sin cambios",
      error: "Error",
      ...(this.props.etiquetasAccion || {}),
    };

    // El Alert de resumen conserva su redacción original salvo que la pantalla haya
    // pasado etiquetas propias; ahí se listan sólo las acciones con etiqueta (una en
    // null significa que esa acción no aplica en esa pantalla).
    const resumenPartes =
      resumenAcciones && this.props.etiquetasAccion
        ? [
            { etiqueta: etiquetas.crear, valor: resumenAcciones.crear },
            { etiqueta: etiquetas.editar, valor: resumenAcciones.editar },
            { etiqueta: etiquetas.sinCambios, valor: resumenAcciones.sinCambios },
            ...(resumenAcciones.errores > 0
              ? [{ etiqueta: "Con errores", valor: resumenAcciones.errores }]
              : []),
          ].filter((parte) => parte.etiqueta)
        : null;

    const columnaIndice = {
      title: "#",
      key: "_rowIndex",
      width: 56,
      fixed: "left",
      align: "center",
      render: (_, __, index) => (
        <span style={{ color: "#8c8c8c", fontVariantNumeric: "tabular-nums" }}>
          {index + 1}
        </span>
      ),
    };

    const columnasConPermisos = permitirEdicion
      ? [
          columnaIndice,
          {
            title: "Acción",
            key: "_accion",
            width: 110,
            fixed: "left",
            render: (_, record) => {
              if (record._accion === "crear")
                return (
                  <span style={{ color: "#52c41a", fontWeight: 500 }}>
                    {etiquetas.crear}
                  </span>
                );
              if (record._accion === "editar")
                return (
                  <span style={{ color: "#faad14", fontWeight: 500 }}>
                    {etiquetas.editar}
                  </span>
                );
              if (record._accion === "error")
                return (
                  <span style={{ color: "#ff4d4f", fontWeight: 500 }}>
                    {etiquetas.error}
                  </span>
                );
              return <span style={{ color: "#bfbfbf" }}>{etiquetas.sinCambios}</span>;
            },
          },
          ...this.envolverColumnasConHighlight(columnasPreview),
        ]
      : [columnaIndice, ...columnasPreview];

    const data = this.state.data || [];
    const dataMostrar =
      permitirEdicion && mostrarSwitchSoloCambios && soloConCambios
        ? data.filter((d) => d._accion !== "sinCambios")
        : data;

    const hayAlgoQueImportar = permitirEdicion
      ? data.some((d) => d._accion === "crear" || d._accion === "editar")
      : data.length > 0;

    return (
      <>
        <PageHeader
          onBack={this.atras}
          title={titulo}
          style={{
            border: "1px solid rgb(235, 237, 240)",
            backgroundColor: "white",
            marginBottom: 10,
          }}
          extra={
            this.state.archivoError ? (
              <ButtonItem onClick={this.descargarArchivoErrores}>
                Descargar Errores
              </ButtonItem>
            ) : null
          }
        />
        <div
          style={{
            backgroundColor: "white",
            padding: 20,
            paddingTop: 5,
            border: "1px solid rgb(235, 237, 240)",
            borderRadius: 8,
          }}
        >
          <Spin
            spinning={this.state.loading}
            tip={
              progreso
                ? `Procesando ${progreso.hechas} de ${progreso.total}, espere por favor`
                : "Procesando, espere por favor"
            }
          >
            {progreso && (
              <Progress
                percent={
                  progreso.total > 0
                    ? Math.round((progreso.hechas / progreso.total) * 100)
                    : 0
                }
                status="active"
                style={{ marginBottom: 12 }}
              />
            )}
            {this.renderInstrucciones()}
            {this.props.controlesExtra && (
              <div style={{ marginBottom: 12 }}>{this.props.controlesExtra}</div>
            )}
            <Form
              ref={this.formRef}
              name="importadorGenerico"
              layout
              bottom={
                <ButtonItem
                  type="primary"
                  htmlType="submit"
                  disabled={!hayAlgoQueImportar}
                  style={{ display: "block", margin: "0 0 0 auto" }}
                >
                  Importar
                </ButtonItem>
              }
              onFinish={this.onFinish}
              onFinishFailed={this.onFinishFailed}
            >
              <Row style={{ marginTop: 20 }} gutter={[16, 16]}>
                <Col
                  xs={24}
                  sm={24}
                  lg={12}
                  xl={12}
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <ButtonItem onClick={this.descargarPlantilla} type="primary">
                    Descargar Plantilla
                  </ButtonItem>
                </Col>
                <Col xs={24} sm={24} lg={12} xl={12}>
                  <FormItem name="archivo" label="Archivo">
                    <Upload
                      showUploadList={{
                        showRemoveIcon: false,
                        showPreviewIcon: false,
                      }}
                      fileList={this.state.archivosExcel}
                      customRequest={this.onChangeArchivo}
                      accept=".xlsx"
                    >
                      <ButtonItem icon={<UploadOutlined />}>Subir</ButtonItem>
                    </Upload>
                  </FormItem>
                </Col>
              </Row>
            </Form>

            {erroresResumen && (
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message={`Se encontraron errores en ${erroresResumen.filasConError} de ${erroresResumen.filasOriginales} filas`}
                description={`Se procesarán únicamente las ${erroresResumen.filasValidas} filas válidas. Descarga "errores.xlsx" para revisar el detalle.`}
              />
            )}

            {permitirEdicion && resumenAcciones && (
              <Alert
                style={{ marginBottom: 12 }}
                type={resumenAcciones.errores > 0 ? "warning" : "info"}
                showIcon
                message={
                  <>
                    {resumenPartes ? (
                      resumenPartes.map((parte, i) => (
                        <React.Fragment key={parte.etiqueta}>
                          {i > 0 && " · "}
                          <b>{parte.valor}</b> {parte.etiqueta}
                        </React.Fragment>
                      ))
                    ) : (
                      <>
                        <b>{resumenAcciones.crear}</b> a crear,{" "}
                        <b>{resumenAcciones.editar}</b> a editar,{" "}
                        <b>{resumenAcciones.sinCambios}</b> sin cambios
                      </>
                    )}
                    {!resumenPartes && resumenAcciones.errores > 0 && (
                      <>
                        , <b>{resumenAcciones.errores}</b> con errores
                      </>
                    )}
                    .
                  </>
                }
                description={
                  resumenAcciones.editar > 0
                    ? 'En las filas a "Editar", las celdas que cambian muestran el valor nuevo y debajo, en gris tachado, el valor anterior.'
                    : undefined
                }
              />
            )}

            {data.length > 0 && (
              <div
                style={{
                  marginBottom: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                {permitirEdicion && mostrarSwitchSoloCambios && (
                  <span>
                    <Switch
                      checked={soloConCambios}
                      onChange={(v) => this.setState({ soloConCambios: v })}
                      size="small"
                    />{" "}
                    Mostrar solo filas con cambios
                  </span>
                )}
                <span style={{ color: "#595959" }}>
                  Mostrando <b>{dataMostrar.length}</b>
                  {dataMostrar.length !== data.length && (
                    <span style={{ color: "#8c8c8c" }}> de {data.length}</span>
                  )}{" "}
                  {dataMostrar.length === 1 ? "fila" : "filas"}
                </span>
              </div>
            )}

            <TableItem
              size="small"
              dataSource={dataMostrar}
              columns={columnasConPermisos}
              scroll={{ x: "max-content" }}
              rowKey={(_, idx) => idx}
            />
          </Spin>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ImportadorExcelGenerico);
